import type {
  ChatBudget,
  ChatLeadSubmission,
  ChatService,
  ChatTimeframe,
} from "../../functions/shared/chat-widget";
import { isValidBrazilianPhone, phoneDigits } from "../../functions/shared/chat-widget";

export const FLOW_STEPS = [
  "welcome",
  "services",
  "project",
  "timeframe",
  "budget",
  "name",
  "phone",
  "email",
  "success",
] as const;

export type FlowStep = (typeof FLOW_STEPS)[number];

export interface ChatAnswers {
  readonly services: readonly ChatService[];
  readonly project: string;
  readonly timeframe: ChatTimeframe | "";
  readonly budget: ChatBudget | "";
  readonly name: string;
  readonly phone: string;
  readonly email: string;
}

export const EMPTY_ANSWERS: ChatAnswers = {
  services: [],
  project: "",
  timeframe: "",
  budget: "",
  name: "",
  phone: "",
  email: "",
};

export const ANSWER_STEPS = FLOW_STEPS.slice(1, 8) as readonly Exclude<
  FlowStep,
  "welcome" | "success"
>[];

export const previousStep = (step: FlowStep): FlowStep => {
  const index = FLOW_STEPS.indexOf(step);
  return FLOW_STEPS[Math.max(0, index - 1)] ?? "welcome";
};

export const stepProgress = (step: FlowStep): number => {
  if (step === "welcome") return 0;
  if (step === "success") return 100;
  const index = Math.min(7, Math.max(1, FLOW_STEPS.indexOf(step)));
  return Math.round((index / 7) * 100);
};

export const questionTypingDelay = (text: string): number => {
  const length = Array.from(text.trim()).length;
  if (!length) return 0;
  // Uma pequena pausa inicial e um tempo proporcional ao texto, sem esperas longas.
  return Math.min(5000, Math.max(1600, 450 + length * 55));
};

export const answerForStep = (step: (typeof ANSWER_STEPS)[number], answers: ChatAnswers): string => {
  if (step === "services") return answers.services.join(", ");
  return answers[step];
};

export const formatBrazilianPhone = (value: string): string => {
  const digits = phoneDigits(value).slice(0, 11);
  if (digits.length <= 2) return digits ? "(" + digits : "";
  if (digits.length <= 6) return "(" + digits.slice(0, 2) + ") " + digits.slice(2);
  if (digits.length <= 10) {
    return (
      "(" +
      digits.slice(0, 2) +
      ") " +
      digits.slice(2, 6) +
      "-" +
      digits.slice(6)
    );
  }
  return (
    "(" +
    digits.slice(0, 2) +
    ") " +
    digits.slice(2, 7) +
    "-" +
    digits.slice(7)
  );
};

export const validateTextStep = (
  step: "project" | "name" | "phone" | "email",
  value: string,
): string | null => {
  const clean = value.trim();
  if (step === "project") {
    if (clean.length < 10) return "Conte um pouco mais — pelo menos 10 caracteres.";
    if (clean.length > 1000) return "Use no máximo 1.000 caracteres.";
  }
  if (step === "name" && clean.length < 2) return "Informe seu nome.";
  if (step === "phone" && !isValidBrazilianPhone(clean)) {
    return "Informe um telefone com DDD.";
  }
  if (
    step === "email" &&
    clean &&
    (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(clean) || clean.length > 120)
  ) {
    return "Informe um e-mail válido ou deixe em branco.";
  }
  return null;
};

export const createSubmission = (
  submissionId: string,
  answers: ChatAnswers,
  pageUrl: string,
): ChatLeadSubmission => {
  if (!answers.timeframe || !answers.budget) {
    throw new Error("Briefing incompleto.");
  }
  return {
    submissionId,
    services: answers.services,
    project: answers.project.trim(),
    timeframe: answers.timeframe,
    budget: answers.budget,
    name: answers.name.trim(),
    phone: answers.phone.trim(),
    email: answers.email.trim(),
    pageUrl,
  };
};
